import { cn } from "@foreday/ui/lib/utils";
import type * as React from "react";
import { createContext, use, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { isTyping } from "@/client/lib/keyboard/shortcut-handler";

interface Slot {
  /** The column beside the main panel that a task, a leave ask or a day opens in. */
  readonly node: HTMLElement | null;
  /** Counts a panel as open until the returned function lets it go. */
  readonly claim: (close: () => void) => () => void;
  /** Shown and no longer inert: what is inside can take focus. */
  readonly open: boolean;
}

export const SideSlotContext = createContext<Slot | null>(null);

/**
 * One right slot for every panel (`docs/design/shell/panels-at-every-width.md`): the shell owns its
 * place, width and entrance, so moving between items changes only what is inside.
 */
export const SLOT_COLUMN =
  "flex w-[var(--side-slot-w)] shrink-0 flex-col overflow-hidden rounded-panel border border-edge bg-linear-to-b from-secondary-from to-secondary-to shadow-lift-1 max-md:transition-[opacity,translate] max-md:duration-[var(--duration-base)] max-md:ease-ui max-md:starting:translate-x-4 max-md:starting:opacity-0";

/**
 * A panel in the shell's slot. Where there is none, on a phone, it covers the screen instead.
 * Escape from the panel or the page beside it closes it, once an open menu or dialog has taken its
 * own Escape; a field outside the panel keeps it, and a panel covered by another ignores it.
 */
export function InSideSlot({
  labelledBy,
  onClose,
  children,
}: {
  readonly labelledBy: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}) {
  const slot = use(SideSlotContext);
  const own = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  const claim = slot?.claim;
  useEffect(() => claim?.(() => close.current()), [claim]);
  // Closing hands focus back to what opened the panel, when the panel took it with it. Read before
  // any passive effect, where the panel's contents may take focus.
  const opener = useRef<Element | null>(null);
  useLayoutEffect(() => {
    opener.current = document.activeElement;
  }, []);
  useEffect(
    () => () => {
      const lost = document.activeElement === document.body;
      const back = opener.current;
      if (lost && back instanceof HTMLElement && back.isConnected) back.focus();
    },
    [],
  );
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const node = own.current;
      if (event.key !== "Escape" || event.defaultPrevented || !node) return;
      if (!node.checkVisibility()) return;
      if (!node.contains(event.target as Node) && isTyping(event.target)) {
        return;
      }
      close.current();
    };
    // On the window, after the document's listeners, where menus and dialogs take theirs.
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const panel = (
    <aside
      ref={own}
      aria-labelledby={labelledBy}
      className={cn(
        "flex min-h-0 flex-1 flex-col",
        !slot && "fixed inset-0 z-40 overflow-y-auto bg-popover",
      )}
    >
      {children}
    </aside>
  );
  if (!slot) return createPortal(panel, document.body);
  return slot.node ? createPortal(panel, slot.node) : null;
}
